'use client';

import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

import type {
  TooltipProps,
  TooltipRefs,
  Direction,
  ArrowLocation,
  PortalTooltipProps,
} from './tooltip.types';
import { usePortalPositioning } from './use-portal-positioning';
import { usePositioning } from './use-default-positioning';

export type { TooltipProps, Direction, ArrowLocation };

const ARROW_STYLES = {
  base: 'before:absolute before:border-[6px]',
  directions: {
    bottom:
      'before:border-l-transparent before:border-r-transparent before:border-t-transparent',
    top: 'before:border-l-transparent before:border-r-transparent before:border-b-transparent',
    left: 'before:border-b-transparent before:border-r-transparent before:border-t-transparent',
    right:
      'before:border-b-transparent before:border-l-transparent before:border-t-transparent',
  },
  positions: {
    bottom: {
      default: '',
      middle: 'before:left-1/2 before:-translate-x-1/2',
      left: 'before:left-[7px]',
      right: 'before:right-[7px]',
      top: '',
      bottom: '',
    },
    top: {
      default: '',
      middle: 'before:left-1/2 before:-translate-x-1/2',
      left: 'before:left-[7px]',
      right: 'before:right-[7px]',
      top: '',
      bottom: '',
    },
    left: {
      default: '',
      middle: 'before:top-1/2 before:-translate-y-1/2',
      left: '',
      right: '',
      top: 'before:top-[7px]',
      bottom: 'before:bottom-[7px]',
    },
    right: {
      default: '',
      middle: 'before:top-1/2 before:-translate-y-1/2',
      left: '',
      right: '',
      top: 'before:top-[7px]',
      bottom: 'before:bottom-[7px]',
    },
  } as const,
  arrowPositions: {
    bottom: 'before:-top-[12px]',
    top: 'before:-bottom-[12px]',
    left: 'before:-right-[12px]',
    right: 'before:-left-[12px]',
  } as const,
};

const BASE_TOOLTIP_CLASSES =
  'normal-case pointer-events-none h-fit z-[110] bg-surface-3 px-2 py-1 text-sm font-normal leading-5 text-surface-3-label transition-opacity rounded shadow-md before:border-surface-3';

function getArrowClasses(direction: Direction, arrowLocation: ArrowLocation) {
  return clsx(
    ARROW_STYLES.base,
    ARROW_STYLES.directions[direction],
    ARROW_STYLES.positions[direction][arrowLocation],
    ARROW_STYLES.arrowPositions[direction],
  );
}

function getWidthClasses(
  maxWidth?: string,
  tooltipWidth?: number,
  isPortal = false,
) {
  if (isPortal) {
    return maxWidth
      ? `${maxWidth} w-max whitespace-normal`
      : 'w-fit min-w-fit whitespace-pre';
  }

  const isOverflowing =
    typeof window !== 'undefined' &&
    tooltipWidth &&
    tooltipWidth > window.innerWidth;

  if (isOverflowing) {
    return 'max-w-[calc(100vw-32px)] break-words';
  }

  return maxWidth
    ? `${maxWidth} w-max whitespace-normal`
    : 'w-fit min-w-fit whitespace-pre';
}

function PortalTooltip({
  show,
  direction,
  arrowLocation,
  children,
  className,
  maxWidth,
  targetRef,
  forwardedRef,
}: PortalTooltipProps) {
  const { refs, floatingStyles, isPositioned } = usePortalPositioning(
    show,
    direction,
    targetRef,
  );

  if (!show) {
    return null;
  }

  const setFloatingRef = (node: HTMLDivElement | null) => {
    if (forwardedRef && typeof forwardedRef === 'object') {
      (forwardedRef as React.MutableRefObject<HTMLDivElement | null>).current =
        node;
    }
    refs.setFloating(node);
  };

  const tooltipClassName = clsx(
    BASE_TOOLTIP_CLASSES,
    getArrowClasses(direction, arrowLocation),
    getWidthClasses(maxWidth, undefined, true),
    isPositioned ? 'opacity-100' : 'opacity-0',
    className,
  );

  return createPortal(
    <div
      ref={setFloatingRef}
      className={tooltipClassName}
      style={floatingStyles}
    >
      {children}
    </div>,
    document.body,
  );
}

// ============================================================================
// Default Mode Renderer
// ============================================================================

interface DefaultTooltipProps {
  show: boolean;
  initialDirection: Direction;
  arrowLocation: ArrowLocation;
  children: React.ReactNode;
  className?: string;
  maxWidth?: string;
  targetRef?: React.RefObject<HTMLElement | null>;
  customPosition?: TooltipProps['customPosition'];
  forwardedRef?: React.ForwardedRef<TooltipRefs>;
}

function DefaultTooltip({
  show,
  initialDirection,
  arrowLocation,
  children,
  className,
  maxWidth,
  targetRef,
  customPosition,
  forwardedRef,
}: DefaultTooltipProps) {
  const internalRef = useRef<HTMLDivElement>(null);
  const tooltipRef =
    (forwardedRef as React.RefObject<HTMLDivElement>) || internalRef;

  const { direction, position, tooltipWidth } = usePositioning(
    show,
    initialDirection,
    arrowLocation,
    targetRef,
    tooltipRef,
    customPosition,
    children,
  );

  const tooltipStyle: React.CSSProperties = {
    ...(targetRef?.current ? position || {} : customPosition || {}),
  };

  const tooltipClassName = clsx(
    BASE_TOOLTIP_CLASSES,
    getArrowClasses(direction, arrowLocation),
    getWidthClasses(maxWidth, tooltipWidth, false),
    show ? 'opacity-100' : 'opacity-0',
    targetRef?.current ? 'fixed' : 'absolute',
    className,
  );

  return (
    <div ref={tooltipRef} className={tooltipClassName} style={tooltipStyle}>
      {children}
    </div>
  );
}

export const Tooltip = React.forwardRef<TooltipRefs, TooltipProps>(
  (
    {
      show = true,
      direction = 'bottom',
      arrowLocation = 'middle',
      children,
      className,
      maxWidth,
      targetRef,
      customPosition,
      portal = false,
    },
    ref,
  ) => {
    if (portal) {
      return (
        <PortalTooltip
          show={show}
          direction={direction}
          arrowLocation={arrowLocation}
          children={children}
          className={className}
          maxWidth={maxWidth}
          targetRef={targetRef}
          forwardedRef={ref}
        />
      );
    }

    return (
      <DefaultTooltip
        show={show}
        initialDirection={direction}
        arrowLocation={arrowLocation}
        children={children}
        className={className}
        maxWidth={maxWidth}
        targetRef={targetRef}
        customPosition={customPosition}
        forwardedRef={ref}
      />
    );
  },
);

Tooltip.displayName = 'Tooltip';
